'use strict';

var InternalSnackbar_web_types = require('../../internal/InternalSnackbar/InternalSnackbar.web.types.cjs');
var snackbarStore = require('./snackbarStore.cjs');

/**
 * Hook for managing snackbar notifications
 *
 * Provides methods to display snackbar messages with different variants (info, success, critical).
 * Snackbars auto-dismiss after 6 seconds unless marked as pending, which requires manual dismissal.
 *
 * @returns Object containing snackbar display methods and message removal
 * @returns {Function} showSnackbar - Display a snackbar with custom variant
 * @returns {Function} successSnackbar - Display a success snackbar
 * @returns {Function} criticalSnackbar - Display a critical/error snackbar
 * @returns {Function} infoSnackbar - Display an info snackbar
 * @returns {Function} removeMessage - Manually dismiss a snackbar by its ID
 *
 * {@link https://aviary.fullscript.io/latest/components/snackbar/web-MEMTPTxQ | Documentation}
 *
 * {@link https://git.fullscript.io/developers/aviary/-/blob/development/packages/aviary-web/src/components/molecules/Snackbar/useSnackbar.ts | View in GitLab}
 *
 * @scope Core
 *
 * @example
 * ```tsx
 * const { successSnackbar, removeMessage, showSnackbar } = useSnackbar();
 *
 * // Show a success message
 * successSnackbar("Changes saved successfully");
 *
 * // Show with action button and custom position
 * successSnackbar("File uploaded", {
 *   actionText: "View",
 *   onAction: () => console.log("View clicked"),
 *   position: SNACKBAR_POSITIONS.topCenter
 * });
 *
 * // Show pending state
 * const id = showSnackbar("Saving...", {
 *   isPending: true,
 *   variant: SNACKBAR_VARIANTS.info
 * });
 * // Later dismiss it
 * removeMessage(id);
 * ```
 */ const useSnackbar = ()=>{
    const { addMessage, removeMessage } = snackbarStore.useSnackbarStore();
    const showSnackbar = (message, options)=>{
        return addMessage({
            message,
            variant: options?.variant || InternalSnackbar_web_types.SNACKBAR_VARIANTS.info,
            position: options?.position || InternalSnackbar_web_types.SNACKBAR_POSITIONS.bottomRight,
            duration: options?.isPending ? 0 : 6000,
            actionText: options?.actionText,
            onAction: options?.onAction,
            isPending: options?.isPending
        });
    };
    const successSnackbar = (message, options)=>showSnackbar(message, {
            ...options,
            variant: InternalSnackbar_web_types.SNACKBAR_VARIANTS.success
        });
    const criticalSnackbar = (message, options)=>showSnackbar(message, {
            ...options,
            variant: InternalSnackbar_web_types.SNACKBAR_VARIANTS.critical
        });
    const infoSnackbar = (message, options)=>showSnackbar(message, {
            ...options,
            variant: InternalSnackbar_web_types.SNACKBAR_VARIANTS.info
        });
    return {
        showSnackbar,
        successSnackbar,
        criticalSnackbar,
        infoSnackbar,
        removeMessage
    };
};

exports.useSnackbar = useSnackbar;
//# sourceMappingURL=useSnackbar.cjs.map
